// FASE 2 · datos de partidos en vivo para 09 Inicio / 04 Señales / 10
// Detalle. Paridad con el dashboard: /live_games (useLiveGames) + /picks
// (nombres completos, proyección, edge_history, stats) + /active_signals
// (alertas EG1 vigentes, useActiveSignals). Nada se calcula en paralelo
// al motor: el edge mostrado es proyección − línea cruda (misma regla que
// GameRow.edgeDisplay / LiveCard), y el estado sale de categoria_dashboard.
//
// Mapeo de estados (aprobado por Matías 2026-09-10):
//   ENTRAR   ← senal / señal / enter / telegram_emit
//   VIGILAR  ← vigilar / watch (también RADAR+VIGILAR)
//   TEMPRANO ← temprano / early  (muestra insuficiente)
//   ESPERAR  ← esperar / wait / descartado / bloqueado / sin línea
//   RADAR queda reservado para las alertas EG1 (/radar_vip_results).
//
// Umbral real por deporte (decisión 2): básquet ±10 · handball +3 / −2.
import { useEffect, useMemo, useState } from 'react';
import { onValue, ref } from 'firebase/database';
import { db } from '../../firebase';
import { useLiveGames } from '../../hooks/useLiveGames';
import { useActiveSignals } from '../../hooks/useActiveSignals';

export const HB_RE = /handball|hbl|ehf|asobal|starligue|handbold|handboll/i;
export const esHandballDe = (g) => g?.match_type === 'handball' || g?.sport === 'handball' || HB_RE.test(String(g?.liga || g?.league || ''));

// Umbral de señal por deporte y lado (puntos de edge).
export function umbralDe(esHb, edge) {
  if (!esHb) return edge < 0 ? -10 : 10;
  return edge < 0 ? -2 : 3;
}
export const fmtNum = (n, dec = 1) => {
  const v = parseFloat(n);
  if (Number.isNaN(v)) return '—';
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(dec)}`;
};
export const fmtSigned = fmtNum;

// "Q2 · 06:32" (básquet) · "2T · 35'" (handball)
export function tiempoDe(g, esHb) {
  if (esHb) {
    const min = g.minuto != null ? `${Math.round(g.minuto)}'` : (g.reloj || '—');
    return `${Number(g.periodo) === 2 ? '2T' : '1T'} · ${min}`;
  }
  const per = Number(g.periodo || g.period);
  const q = per >= 1 ? (per <= 4 ? `Q${per}` : `OT${per - 4}`) : '—';
  const r = String(g.reloj || g.clock || '').replace(/\s*min restantes/, '');
  return r && r !== '-' ? `${q} · ${/^\d+$/.test(r) ? `${r}:00` : r}` : q;
}

// Progreso del partido 0-100 para la barra --gp de la card .lv
export function progresoDe(g, esHb, pick) {
  if (esHb) {
    const m = g.minuto != null ? Number(g.minuto) : (Number(g.periodo) === 2 ? 45 : 15);
    return Math.max(2, Math.min(100, Math.round((m / 60) * 100)));
  }
  const dur = Number(pick?.duracion) || 40;
  const mp = String(pick?.min_jugados || '').split('/')[0];
  const jug = parseFloat(mp);
  if (!Number.isNaN(jug) && jug >= 0) return Math.max(2, Math.min(100, Math.round((jug / dur) * 100)));
  const per = Number(g.periodo) || 1;
  return Math.max(2, Math.min(100, Math.round(((per - 0.5) / 4) * 100)));
}

const CAT = {
  senal: 'ENTRAR', 'señal': 'ENTRAR', enter: 'ENTRAR', entrar: 'ENTRAR',
  vigilar: 'VIGILAR', watch: 'VIGILAR', radar: 'VIGILAR',
  temprano: 'TEMPRANO', early: 'TEMPRANO',
  esperar: 'ESPERAR', wait: 'ESPERAR', descartado: 'ESPERAR', bloqueado: 'ESPERAR', blacklist: 'ESPERAR',
};
export function estadoDe(g, hasLine) {
  if (!hasLine) return 'ESPERAR';
  const key = String(g.categoria_dashboard || (g.v6 && g.v6.categoria) || '').toLowerCase();
  if (CAT[key]) return CAT[key];
  if (g.telegram_emit) return 'ENTRAR';
  return 'ESPERAR';
}
export const STT_CLS = { ENTRAR: 'g', VIGILAR: 'w', TEMPRANO: 'n', ESPERAR: 'n' };
export const STT_TXT = { ENTRAR: 'Entrar', VIGILAR: 'Vigilar', TEMPRANO: 'Temprano', ESPERAR: 'Esperar' };

// ── /picks completo (una sola suscripción compartida por consumidor) ──
export function usePicks() {
  const [picks, setPicks] = useState(null); // null = cargando
  useEffect(() => {
    const unsub = onValue(ref(db, 'picks'), (s) => setPicks(s.val() || {}), () => setPicks({}));
    return () => unsub();
  }, []);
  return picks;
}
export function pickDe(picks, gid) {
  if (!picks || !gid) return null;
  const id = String(gid);
  const clean = id.replace(/^aps_/, '');
  return picks[id] || picks[`aps_${clean}`] || picks[clean]
    || Object.values(picks).find((p) => String(p?.game_id || '') === id) || null;
}
export function nombresDe(g, pick) {
  const full = pick && typeof pick.partido === 'string' && pick.partido.includes(' @ ') ? pick.partido.split(' @ ') : null;
  const away = (full && full[0] && full[0].trim()) || g.away_team || pick?.away_team || '?';
  const home = (full && full[1] && full[1].trim()) || g.home_team || pick?.home_team || '?';
  return { away, home };
}

// ── partidos en vivo enriquecidos ─────────────────────────────────────
export function useLiveV3() {
  const { games, loading, error } = useLiveGames();
  const picks = usePicks();
  const { active } = useActiveSignals(picks || {});
  const activeSet = useMemo(() => new Set(active.map((a) => String(a.id))), [active]);

  const list = useMemo(() => games.map((g) => {
    const gid = String(g.game_id);
    const pick = pickDe(picks, gid);
    const esHb = esHandballDe(g) || esHandballDe(pick);
    const lineaNum = parseFloat(g.linea);
    const hasLine = !Number.isNaN(lineaNum) && lineaNum > 0;
    const proj = parseFloat(g.projection ?? pick?.proyeccion);
    const hasProj = !Number.isNaN(proj) && proj > 0;
    const edge = hasLine && hasProj ? proj - lineaNum : null; // cruda (paridad)
    const estado = estadoDe(g, hasLine);
    const { away, home } = nombresDe(g, pick);
    const m = g.marcador || {};
    const umbral = edge != null ? umbralDe(esHb, edge) : null;
    const falta = edge != null ? Math.max(0, Math.abs(umbral) - Math.abs(edge)) : null;
    const lado = edge == null ? null : edge < 0 ? 'UNDER' : 'OVER';
    return {
      gid, raw: g, pick, esHb, liga: g.liga || pick?.liga || '—',
      away, home, marcador: { away: m.away ?? null, home: m.home ?? null, total: m.total ?? null },
      periodo: Number(g.periodo || g.period) || null, reloj: g.reloj || '', minuto: g.minuto ?? pick?.minuto ?? null,
      tiempo: tiempoDe(g, esHb), gp: progresoDe(g, esHb, pick),
      linea: hasLine ? lineaNum : null, proyeccion: hasProj ? proj : null, edge, lado,
      estado, umbral, falta, radarActiva: activeSet.has(gid),
      razon: (g.v6 && g.v6.razon_humana) || pick?.razon || '',
      sinLinea: !hasLine,
    };
  }), [games, picks, activeSet]);

  return { live: list, loading: loading || picks === null, error, picks };
}

// id de partido de un registro de signals_history (básquet trae pick_id,
// handball game_id); último recurso: el id del registro.
export const gidDeSenal = (s) => String(s?.pick_id || s?.game_id || s?.id || '');
// Nombre corto de equipo: última palabra ("Los Angeles Lakers" → "Lakers");
// si la última es un sufijo corto ("Korea W", "Real II") conserva dos.
export function shortName(n) {
  const s = String(n || '').trim(); const w = s.split(/\s+/);
  if (s.length <= 12 || w.length === 1) return s;
  const last = w[w.length - 1];
  return last.length <= 3 && w.length >= 2 ? w.slice(-2).join(' ') : last;
}

// Pick "UNDER 162.5" de un partido en vivo según su edge actual
export const pickLive = (l) => (l.lado && l.linea != null ? `${l.lado} ${l.linea}` : '—');

// ── seguir partido (10 · sticky CTA) — localStorage; aviso local al cerrar
//    mientras la app esté abierta (no hay push por partido en backend) ──
const FOLLOW_KEY = 'ee-app-follow';
function readFollow() { try { return JSON.parse(localStorage.getItem(FOLLOW_KEY) || '{}'); } catch (_e) { return {}; } }
export function useFollow(gid) {
  const [map, setMap] = useState(readFollow);
  const on = !!map[gid];
  const toggle = (meta) => {
    const n = { ...map };
    if (n[gid]) delete n[gid]; else n[gid] = { ...meta, at: Date.now() };
    try { localStorage.setItem(FOLLOW_KEY, JSON.stringify(n)); } catch (_e) { /* noop */ }
    setMap(n);
    if (!on && typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission().catch(() => {});
  };
  return { on, toggle };
}
// Vigila los partidos seguidos: cuando salen de /live_games (cerraron)
// dispara una notificación local y limpia el seguimiento.
export function useFollowAlerts(liveIds) {
  useEffect(() => {
    const map = readFollow();
    const ids = Object.keys(map);
    if (!ids.length || !liveIds) return;
    let changed = false;
    for (const id of ids) {
      if (liveIds.has(id)) continue;
      if (Date.now() - (map[id].at || 0) < 60e3) continue; // recién marcado: esperar 1 ciclo real
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try { new Notification('Partido cerrado', { body: `${map[id].partido || 'Tu partido'} terminó. Revisa el resultado en la app.` }); } catch (_e) { /* noop */ }
      }
      delete map[id]; changed = true;
    }
    if (changed) { try { localStorage.setItem(FOLLOW_KEY, JSON.stringify(map)); } catch (_e) { /* noop */ } }
  }, [liveIds]);
}
